import { useState, useCallback, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, MapPin, Bed, Bath, Square, DollarSign,
  TrendingUp, ArrowRight, Building2, Filter, Loader2, ChevronDown,
  Share2, Copy, Check, Bookmark, BookmarkCheck, X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import logger from "@/utils/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const formatPrice = (price) => {
  if (!price) return "N/A";
  if (price >= 1000000) return `$${(price / 1000000).toFixed(2)}M`;
  return `$${price.toLocaleString()}`;
};

export default function ReplacementFinder({ embedded }) {
  const navigate = useNavigate();
  const [salePrice, setSalePrice] = useState("");
  const [market, setMarket] = useState("all");
  const [minBeds, setMinBeds] = useState("");
  const [minCapRate, setMinCapRate] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Share state
  const [sharing, setSharing] = useState(false);
  const [shareModal, setShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [creatorEmail, setCreatorEmail] = useState("");

  // Save state
  const [user, setUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveLabel, setSaveLabel] = useState("");
  const [saveModal, setSaveModal] = useState(false);

  // Check auth on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(res.data);
      } catch (err) { logger.error("Auth check failed:", err); }
    })();
  }, []);

  const handleSearch = useCallback(async (e) => {
    e?.preventDefault();
    setLoading(true);
    setHasSearched(true);
    setSaved(false);
    try {
      const params = new URLSearchParams();
      if (salePrice) params.append("sale_price", salePrice);
      if (market !== "all") params.append("market", market);
      if (minBeds && minBeds !== "any") params.append("min_beds", minBeds);
      if (minCapRate && minCapRate !== "any") params.append("min_cap_rate", minCapRate);
      const response = await axios.get(`${API}/replacement-finder?${params.toString()}`);
      setResults(response.data);
    } catch (err) {
      logger.error("Search failed:", err);
      setResults({ count: 0, properties: [] });
    } finally {
      setLoading(false);
    }
  }, [salePrice, market, minBeds, minCapRate]);

  const handleShare = async () => {
    setSharing(true);
    try {
      const payload = {
        sale_price: salePrice || null,
        market: market !== "all" ? market : null,
        min_beds: (minBeds && minBeds !== "any") ? parseInt(minBeds) : null,
        min_cap_rate: (minCapRate && minCapRate !== "any") ? parseFloat(minCapRate) : null,
        creator_email: creatorEmail || (user?.email) || null,
      };
      const res = await axios.post(`${API}/replacement-finder/share`, payload, { withCredentials: true });
      const url = `${BASE_URL}/replacement-finder/shared/${res.data.share_id}`;
      setShareUrl(url);
      setShareModal(true);
    } catch (err) {
      logger.error("Share failed:", err);
      toast.error("Failed to create share link");
    } finally {
      setSharing(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback
      const ta = document.createElement("textarea");
      ta.value = shareUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const handleSave = async () => {
    if (!user) {
      toast.info("Sign in to save searches to your account");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        label: saveLabel || `Search ${new Date().toLocaleDateString()}`,
        sale_price: salePrice || null,
        market: market !== "all" ? market : null,
        min_beds: (minBeds && minBeds !== "any") ? parseInt(minBeds) : null,
        min_cap_rate: (minCapRate && minCapRate !== "any") ? parseFloat(minCapRate) : null,
      };
      await axios.post(`${API}/replacement-finder/save`, payload, { withCredentials: true });
      setSaved(true);
      setSaveModal(false);
      toast.success("Search saved to your account");
    } catch {
      toast.error("Failed to save search");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={embedded ? "" : "min-h-screen bg-slate-50"}>
      {/* Hero */}
      <div className={embedded ? "bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 text-white rounded-2xl" : "bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 text-white"}>
        <div className={`max-w-6xl mx-auto px-6 ${embedded ? "py-10" : "py-16 md:py-24"}`}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="inline-block text-xs font-semibold tracking-widest text-blue-400 uppercase mb-4">1031 Exchange Tool</span>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight leading-tight mb-4 text-white">Find Your Replacement<br />Property</h1>
            <p className="text-lg text-slate-300 max-w-xl mb-8">
              Enter your sale price and preferences to find qualifying replacement properties for your 1031 exchange.
            </p>
          </motion.div>

          {/* Search Form */}
          <motion.form onSubmit={handleSearch} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }} className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 md:p-8" data-testid="replacement-finder-form">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">Sale / Exchange Amount</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input type="number" value={salePrice} onChange={(e) => setSalePrice(e.target.value)} placeholder="e.g. 3000000" className="pl-9 bg-white/10 border-white/20 text-white placeholder:text-slate-400 h-12" data-testid="sale-price-input" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">Market</label>
                <Select value={market} onValueChange={setMarket}>
                  <SelectTrigger className="bg-white/10 border-white/20 text-white h-12" data-testid="market-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Markets</SelectItem>
                    <SelectItem value="nyc">New York City</SelectItem>
                    <SelectItem value="miami">Miami</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button type="submit" disabled={loading} className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold" data-testid="search-btn">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Search className="w-5 h-5 mr-2" />Find Properties</>}
                </Button>
              </div>
            </div>

            <button type="button" onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-1.5 text-sm text-slate-300 hover:text-white transition-colors mt-2">
              <Filter className="w-3.5 h-3.5" />Advanced Filters
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
              {showFilters && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-white/10">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-1.5">Min Bedrooms</label>
                      <Select value={minBeds} onValueChange={setMinBeds}>
                        <SelectTrigger className="bg-white/10 border-white/20 text-white h-10" data-testid="min-beds-select"><SelectValue placeholder="Any" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Any</SelectItem>
                          <SelectItem value="1">1+</SelectItem>
                          <SelectItem value="2">2+</SelectItem>
                          <SelectItem value="3">3+</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-1.5">Min Cap Rate</label>
                      <Select value={minCapRate} onValueChange={setMinCapRate}>
                        <SelectTrigger className="bg-white/10 border-white/20 text-white h-10" data-testid="min-cap-select"><SelectValue placeholder="Any" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Any</SelectItem>
                          <SelectItem value="3">3%+</SelectItem>
                          <SelectItem value="4">4%+</SelectItem>
                          <SelectItem value="5">5%+</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.form>
        </div>
      </div>

      {/* Results */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        {!hasSearched && (
          <div className="text-center py-16">
            <Building2 className="w-16 h-16 text-slate-200 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Enter your exchange details above</h3>
            <p className="text-slate-500 max-w-md mx-auto">We'll match you with qualifying replacement properties from our curated NYC and Miami inventory.</p>
          </div>
        )}

        {hasSearched && !loading && results && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
            {/* Summary Cards + Share/Save Bar */}
            {results.count > 0 && (
              <>
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
                  <h2 className="text-xl font-bold text-slate-900">
                    {results.count} Matching {results.count === 1 ? "Property" : "Properties"}
                  </h2>
                  <div className="flex items-center gap-2" data-testid="share-save-actions">
                    <Button variant="outline" size="sm" onClick={handleShare} disabled={sharing} className="border-slate-300 hover:bg-slate-50" data-testid="share-btn">
                      {sharing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Share2 className="w-4 h-4 mr-1.5" />}
                      Share Results
                    </Button>
                    {user ? (
                      <Button variant="outline" size="sm" onClick={() => { setSaveLabel(`${market !== "all" ? market.toUpperCase() : "All"} ${salePrice ? "under " + formatPrice(parseFloat(salePrice)) : "properties"}`); setSaveModal(true); }} disabled={saved} className={`border-slate-300 ${saved ? "bg-green-50 border-green-300 text-green-700" : "hover:bg-slate-50"}`} data-testid="save-btn">
                        {saved ? <><BookmarkCheck className="w-4 h-4 mr-1.5" />Saved</> : <><Bookmark className="w-4 h-4 mr-1.5" />Save Search</>}
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => toast.info("Sign in to save searches to your account")} className="border-slate-300 hover:bg-slate-50" data-testid="save-btn-guest">
                        <Bookmark className="w-4 h-4 mr-1.5" />Save Search
                      </Button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                    <p className="text-sm text-slate-500 mb-1">Matching Properties</p>
                    <p className="text-3xl font-bold text-slate-900" data-testid="results-count">{results.count}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                    <p className="text-sm text-slate-500 mb-1">Avg. Est. Cap Rate*</p>
                    <p className="text-3xl font-bold text-green-600" data-testid="avg-cap-rate">{results.avg_cap_rate}%</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                    <p className="text-sm text-slate-500 mb-1">Price Range</p>
                    <p className="text-xl font-bold text-slate-900" data-testid="price-range">
                      {results.price_range ? `${formatPrice(results.price_range.min)} - ${formatPrice(results.price_range.max)}` : "N/A"}
                    </p>
                  </div>
                </div>
              </>
            )}

            {/* Property Cards */}
            {results.count === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
                <Search className="w-14 h-14 text-slate-200 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">No matching properties found</h3>
                <p className="text-slate-500">Try adjusting your filters or increasing your price range.</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="results-list">
                {results.properties.map((prop, idx) => (
                  <motion.div key={prop.property_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: idx * 0.05 }} className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden" data-testid={`result-card-${prop.property_id}`}>
                    <div className="flex flex-col md:flex-row">
                      <div className="w-full md:w-56 h-40 md:h-auto bg-slate-100 flex-shrink-0">
                        {prop.images?.[0] ? <img src={prop.images[0]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Building2 className="w-10 h-10 text-slate-300" /></div>}
                      </div>
                      <div className="flex-1 p-5">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="font-semibold text-slate-900 text-lg">{prop.title}</h3>
                            <div className="flex items-center gap-1.5 text-sm text-slate-500 mt-1"><MapPin className="w-3.5 h-3.5" /><span>{prop.address || prop.location}</span></div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-xl font-bold text-slate-900">{formatPrice(prop.price)}</p>
                            <span className="text-xs font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-600">{prop.market?.toUpperCase()}</span>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-slate-600">
                          {prop.bedrooms > 0 && <span className="flex items-center gap-1"><Bed className="w-4 h-4 text-slate-400" />{prop.bedrooms} bed</span>}
                          {prop.bathrooms > 0 && <span className="flex items-center gap-1"><Bath className="w-4 h-4 text-slate-400" />{prop.bathrooms} bath</span>}
                          {prop.sqft > 0 && <span className="flex items-center gap-1"><Square className="w-4 h-4 text-slate-400" />{prop.sqft?.toLocaleString()} SF</span>}
                        </div>
                        <div className="mt-3 flex flex-wrap items-center gap-4 text-sm">
                          {prop.monthly_rent > 0 && <div><span className="text-slate-400">Rent:</span><span className="ml-1 font-semibold text-slate-700">${prop.monthly_rent.toLocaleString()}/mo</span></div>}
                          {prop.noi !== 0 && <div><span className="text-slate-400">NOI:</span><span className="ml-1 font-semibold text-green-600">${prop.noi.toLocaleString()}/yr</span></div>}
                          <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1"><TrendingUp className="w-4 h-4 text-green-600" /><span className="font-bold text-green-700">Est. {prop.cap_rate}% Cap*</span></div>
                        </div>
                        <div className="mt-4">
                          <Link to={`/properties/${prop.property_id}`}>
                            <Button variant="outline" size="sm" className="text-blue-600 border-blue-200 hover:bg-blue-50">View Details <ArrowRight className="w-4 h-4 ml-1.5" /></Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-700 leading-relaxed">
                <strong>Disclaimer:</strong> Cap rate is an estimated metric based on assumptions regarding income and expenses. It is provided for informational purposes only and does not represent actual or future performance. Results may vary. Krieger Real Estate LLC is a licensed real estate brokerage. We do not provide investment, legal, or tax advice. Buyers should conduct independent due diligence.
              </p>
            </div>
          </motion.div>
        )}
      </div>

      {/* Share Modal */}
      <Dialog open={shareModal} onOpenChange={setShareModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Share2 className="w-5 h-5 text-blue-600" />Share Search Results</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-slate-600">
              Anyone with this link will need to register or sign in to view the full property details and financial analysis.
            </p>
            <div className="flex items-center gap-2">
              <Input value={shareUrl} readOnly className="flex-1 bg-slate-50 text-sm font-mono" data-testid="share-url-input" />
              <Button onClick={handleCopy} variant="outline" size="sm" className={`flex-shrink-0 min-w-[80px] ${copied ? "bg-green-50 border-green-300 text-green-700" : ""}`} data-testid="copy-link-btn">
                {copied ? <><Check className="w-4 h-4 mr-1" />Copied</> : <><Copy className="w-4 h-4 mr-1" />Copy</>}
              </Button>
            </div>
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
              <strong>Live link</strong> — recipients will see the latest matching properties when they open this link.
            </div>
            {!user && !creatorEmail && (
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                <p className="text-sm text-slate-600 mb-2">Want to be notified when someone views this search?</p>
                <Input
                  type="email"
                  value={creatorEmail}
                  onChange={(e) => setCreatorEmail(e.target.value)}
                  placeholder="Enter your email (optional)"
                  className="text-sm"
                  data-testid="creator-email-input"
                />
              </div>
            )}
            {(user || creatorEmail) && (
              <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
                <Check className="w-4 h-4 flex-shrink-0" />
                <span>You'll receive an email notification when someone views this search.</span>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Save Modal */}
      <Dialog open={saveModal} onOpenChange={setSaveModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Bookmark className="w-5 h-5 text-blue-600" />Save Search</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Search Name</label>
              <Input value={saveLabel} onChange={(e) => setSaveLabel(e.target.value)} placeholder="e.g., NYC condos under $3M" data-testid="save-label-input" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setSaveModal(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" data-testid="confirm-save-btn">
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><BookmarkCheck className="w-4 h-4 mr-2" />Save</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
